import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { captureException } from '../sentry/sentry';
import { isNil } from 'lodash';
import { isWeb } from '../../config/designSystem/Layout';

/**
 * Loads a string from storage.
 *
 * @param key The key to fetch.
 */
export const loadString = async (key: string): Promise<string | null> => {
  try {
    if(isWeb) {
      return await AsyncStorage.getItem(key);
    } else {
      return await SecureStore.getItemAsync(key, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
    }
  } catch(error) {
    captureException(error);
    return null;
  }
};

export const loadStringFromChunked = async (key: string): Promise<string | null> => {
  const nbChunk = await SecureStore.getItemAsync(key+'_N', {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
  if(Number(nbChunk) >0) {
    const values = await Promise.all(
      Array.from(Array(Number(nbChunk)).keys()).map(async idx => {
        try {
          const keyIdx = key+'_'+idx;
          return await SecureStore.getItemAsync(keyIdx, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
        } catch(error) {
          console.log(error);
          return '';
        }
      }));
    const red = values.reduce((acc, val) => {
      if(acc !== null) {
        return acc + val;
      } else {
        return val;
      }}, '');
    return red;
  }
  return null;
};

/**
 * Saves a string to storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 */
export const saveString = async (key: string, value: string): Promise<any | null> => {
  try {
    if(isWeb) {
      return await AsyncStorage.setItem(key, value);
    } else {
      return await SecureStore.setItemAsync(key, value, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
    }
  } catch(error) {
    captureException(error);
    return null;
  }
};

export const saveChunkedString = async (key: string, value: string): Promise<void> => {
// chunk value into chunks of 2000 bytes and save every chunk under key_1, key_2, key_3, etc.
  const chunks = chunk(value, 2000);
  const chunksLength = chunks.length;
  if(chunks) {
    await Promise.all(chunks.map(async (chunk,idx) => {
      const keyIdx=   key+'_'+idx;
      return await SecureStore.setItemAsync(keyIdx, chunk, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
    }));
    await SecureStore.setItemAsync(key+'_N', String(chunksLength),{keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
  }
  return;
};

const chunk = (s: string, maxBytes: number) => {
  const values =  s.match(`/.{1,${maxBytes}}/g`);
  if(!isNil(values)) {
    return values;
  } else {
    return []; // should not happen, there to please typescript.
  }
};


export const load = async (key: string, encryptedStore = false ): Promise<any | null> => {
  try {
    if(encryptedStore && !isWeb) {
      const almostThere = await SecureStore.getItemAsync(key, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
      return JSON.parse(String(almostThere));
    } else {
      const almostThere = await AsyncStorage.getItem(key);
      return JSON.parse(String(almostThere));
    }
  } catch(error) {
    captureException(error);
    return null;
  }
};

/**
 * Saves an object to storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 */
export const save = async (key: string, value: any, encryptedStore = false): Promise<boolean> => {
  try {
    if(encryptedStore && !isWeb) {
      await SecureStore.setItemAsync(key, JSON.stringify(value), {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
    } else {
      await AsyncStorage.setItem(key, JSON.stringify(value));
    }
    return true;
  } catch(error) {
    captureException(error);
    return false;
  }
};

/**
 * Removes something from storage.
 *
 * @param key The key to kill.
 */
export const remove = async (key: string): Promise<void> => {
  try {
    if(isWeb) {
      await AsyncStorage.removeItem(key);
    } else {
      await SecureStore.deleteItemAsync(key, {keychainAccessible: SecureStore.WHEN_UNLOCKED_THIS_DEVICE_ONLY});
    }
  } catch(error) {
    captureException(error);
  }
};

/**
 * Burn it all to the ground.
 */
export const clearAsyncStorage = async (): Promise<void> => {
  try {
    if(isWeb){
      await AsyncStorage.clear();
    }
  } catch(error) {
    captureException(error);
  }
};
